import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient, UserMessage, UserMessagesResponse } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/layout';
import { MainSection } from '@/components/layout/MainSection';
import { MESSAGE_TYPES, INVITE_STATUSES } from '@/constants';
const MessagesTab: React.FC = () => {
  const { t } = useTranslation(['account', 'common']);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const options = {
        page: pagination.page,
        limit: pagination.limit,
      };

      const response = await apiClient.getUserMessages(options);

      if (response.data.success) {
        const data: UserMessagesResponse = response.data.data;

        // Filter out accepted/declined invitations from the list
        const filteredMessages = data.messages.filter(msg => {
          if (msg.message_type !== MESSAGE_TYPES.TOURNAMENT_INVITATION) {
            return true;
          }
          return msg.invitation_status === INVITE_STATUSES.PENDING || !msg.invitation_status;
        });

        setMessages(filteredMessages);
        setPagination({
          ...data.pagination,
          total: filteredMessages.length,
        });
      } else {
        throw new Error(response.data.error || 'Failed to load messages');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await apiClient.getUnreadMessageCount();
      if (response.data.success) {
        setUnreadCount(response.data.data.unreadCount);
      }
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  }, []);

  const markAsRead = useCallback(async (messageId: number) => {
    try {
      const response = await apiClient.markMessageAsRead(messageId);
      if (response.data.success) {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === messageId ? { ...msg, is_read: true } : msg
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await apiClient.markAllMessagesAsRead();
      if (response.data.success) {
        setMessages(prev => prev.map(msg => ({ ...msg, is_read: true })));
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Error marking all messages as read:', err);
    }
  }, []);

  const archiveMessage = useCallback(async (messageId: number) => {
    try {
      const message = messages.find(msg => msg.id === messageId);

      if (message && message.message_type === MESSAGE_TYPES.TOURNAMENT_INVITATION && message.action_url) {
        try {
          const inviteCodeMatch = message.action_url.match(/\/invite\/([^/]+)/);
          if (inviteCodeMatch && inviteCodeMatch[1]) {
            const inviteCode = inviteCodeMatch[1];
            await apiClient.declineInvitation(inviteCode);
          }
        } catch (declineError: any) {
          console.error('Error declining invitation:', declineError);
          const errorMessage = declineError.response?.data?.error || declineError.message;
          if (!errorMessage.includes('Invalid or expired')) {
            setError(`Failed to decline invitation: ${errorMessage}`);
            setTimeout(() => setError(null), 5000);
          }
        }
      }

      const response = await apiClient.archiveMessage(messageId);
      if (response.data.success) {
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        if (message && !message.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (err: any) {
      console.error('Error archiving message:', err);
      setError('Failed to delete message');
      setTimeout(() => setError(null), 5000);
    }
  }, [messages]);

  const handleMessageClick = useCallback((message: UserMessage) => {
    if (!message.is_read) {
      markAsRead(message.id);
    }

    const url = message.action_url;
    const isSettledInvitation = message.message_type === MESSAGE_TYPES.TOURNAMENT_INVITATION
      && !!message.invitation_status && message.invitation_status !== INVITE_STATUSES.PENDING;
    if (!url || isSettledInvitation) return;
    // App paths stay in the SPA, so back returns to the messages.
    if (url.startsWith('/')) {
      navigate(url);
    } else {
      window.open(url, '_blank');
    }
  }, [markAsRead, navigate]);

  const getMessageTypeLabel = useCallback((type: string) => {
    const labels: Record<string, string> = {
      tournament_invitation: t('account:messageTypeInvitation'),
      system_notification: t('account:messageTypeNotification'),
      email_status: 'Email',
      tournament_update: t('account:messageTypeTournament'),
    };
    return labels[type] || type;
  }, [t]);

  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Load messages and unread count on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadMessages();
      loadUnreadCount();
    }
  }, [isAuthenticated, loadMessages, loadUnreadCount]);

  const headerRight = (
    <button
      onClick={markAllAsRead}
      disabled={unreadCount === 0}
      className="ls-btn"
      style={{ fontSize: '12px', padding: '6px 12px', opacity: unreadCount === 0 ? 0.5 : 1 }}
    >
      {t('account:markAllRead')}
    </button>
  );

  return (
    <MainSection
      title={t('account:messages')}
      headerRight={headerRight}
    >
      {loading ? null : error ? (
        <Card className="ls-text-center">
          <div className="theme-text-error ls-mb-3">&#x274C; {error}</div>
          <button onClick={() => loadMessages()} className="ls-btn">
            {t('common:retry')}
          </button>
        </Card>
      ) : messages.length === 0 ? (
        <Card className="ls-text-center ls-py-4">
          <div style={{ fontSize: '48px' }} className="ls-mb-2">&#x1F4ED;</div>
          <div className="theme-text-secondary">{t('account:noMessages')}</div>
          <p className="theme-text-muted" style={{ fontSize: '12px' }}>
            {t('account:noMessagesYet')}
          </p>
        </Card>
      ) : (
        <>
          <div className="ls-flex ls-flex-col ls-gap-3">
            {messages.map((message) => (
              <Card
                key={message.id}
                onClick={() => handleMessageClick(message)}
                className={!message.is_read ? 'theme-bg-status-info' : ''}
              >
                <div className="ls-flex ls-align-start ls-justify-between ls-gap-3">
                  <div className="ls-flex-1">
                    <div className="ls-flex ls-align-center ls-gap-2 ls-mb-1">
                      <span className={`ls-label ${!message.is_read ? 'theme-status-info' : ''}`}>
                        {message.title}
                      </span>
                      {!message.is_read && (
                        <span className="ls-tag ls-tag--blue" style={{ fontSize: '10px' }}>{t('account:new')}</span>
                      )}
                    </div>
                    <span className="ls-tag" style={{ fontSize: '10px' }}>{getMessageTypeLabel(message.message_type)}</span>
                    <p className="theme-text-secondary ls-mt-2" style={{ fontSize: '13px' }}>
                      {message.content}
                    </p>

                    {message.tournament_name && (
                      <div className="theme-text-muted ls-mt-2" style={{ fontSize: '12px' }}>
                        &#x1F3C6; {message.tournament_name}
                      </div>
                    )}

                    {message.action_label && (
                      <div className="ls-mt-2">
                        {message.message_type === MESSAGE_TYPES.TOURNAMENT_INVITATION && message.invitation_status && message.invitation_status !== INVITE_STATUSES.PENDING ? (
                          <span className={`ls-tag ${message.invitation_status === INVITE_STATUSES.ACCEPTED ? 'ls-tag--green' : ''}`}>
                            {message.invitation_status === INVITE_STATUSES.ACCEPTED ? `\u2713 ${t('account:accepted')}` : `\u2717 ${t('account:declined')}`}
                          </span>
                        ) : (
                          <span className="ls-tag ls-tag--green">{message.action_label} &rarr;</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="ls-flex ls-flex-col ls-align-end ls-gap-2">
                    <span className="theme-text-muted" style={{ fontSize: '11px' }}>
                      {formatDate(message.created_at)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        archiveMessage(message.id);
                      }}
                      className="theme-text-muted"
                      style={{ fontSize: '14px' }}
                    >
&#x1F5D1;&#xFE0F;
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="ls-flex ls-justify-center ls-align-center ls-gap-3 ls-mt-4">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page <= 1}
                className="ls-btn ls-btn--outlined"
                style={{ fontSize: '12px', opacity: pagination.page <= 1 ? 0.5 : 1 }}
              >
                {t('common:previous')}
              </button>

              <span className="theme-text-secondary" style={{ fontSize: '12px' }}>
                {pagination.page} / {pagination.pages}
              </span>

              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                disabled={pagination.page >= pagination.pages}
                className="ls-btn ls-btn--outlined"
                style={{ fontSize: '12px', opacity: pagination.page >= pagination.pages ? 0.5 : 1 }}
              >
                {t('common:next')}
              </button>
            </div>
          )}
        </>
      )}
    </MainSection>
  );
};

export default MessagesTab;
